import { UserPreview } from '@main/access-control-types';
import type { SchemaToType } from '@main/schema-utils';
import { mkInput, mkOrder, mkType } from '@main/schema-utils';

import { Catalog } from './catalog';
import { DataSiloPluginConfigurations } from './DataSiloPlugin';
import {
  ContainsSensitiveData,
  SiloDiscoveryResultOrderField,
  SiloDiscoveryResultStatus,
} from './enums';
import { PlaintextContext } from './plaintextContext';
import { Plugin } from './plugin';

export const SiloDiscoveryResultKey = mkType({
  name: 'SiloDiscoveryResultKey',
  comment: 'The row definition stored in SiloDiscoveryResult table',
  fields: {
    id: {
      comment: 'The unique ID of the data silo discovery result',
      modelName: 'dataSiloDiscoveryResult',
      type: 'id',
    },
    pluginId: {
      comment: 'The ID of the plugin that found this recommendation',
      type: 'id',
      modelName: 'plugin',
    },
    resourceId: {
      comment: 'The unique identifier for the resource',
      type: 'string',
    },
    organizationId: {
      comment: 'ID of the organization the resource belongs to',
      type: 'id',
      modelName: 'organization',
    },
    status: {
      comment: 'The status of the recommendation',
      type: { SiloDiscoveryResultStatus },
    },
    latestRunTime: {
      comment: 'The latest run time of the recommendation',
      type: 'Date',
    },
    region: {
      comment: 'The AWS Region for data silo if applicable',
      type: 'string',
      optional: true,
    },
    country: {
      comment: 'The ISO country code for the AWS Region if applicable',
      type: 'string',
      optional: true,
    },
    countrySubDivision: {
      comment:
        'The ISO country subdivision code for the AWS Region if applicable',
      type: 'string',
      optional: true,
    },
    title: {
      comment: 'The custom title of the data silo recommendation.',
      type: 'string',
      optional: true,
    },
    plaintextContext: {
      comment: 'The plaintext for the resource.',
      type: 'string',
      optional: true,
    },
    suggestedDataSiloType: {
      comment: 'The suggested data silo type (integrationName from catalog)',
      type: 'string',
    },
  },
});

/** Override type */
export type SiloDiscoveryResultKey = SchemaToType<
  typeof SiloDiscoveryResultKey
>;

export const SiloDiscoveryResult = mkType({
  name: 'SiloDiscoveryResult',
  comment: 'A recommendation result from a silo discovery plugin',
  fields: {
    id: SiloDiscoveryResultKey.fields.id,
    pluginId: SiloDiscoveryResultKey.fields.pluginId,
    plugin: {
      comment: 'The plugin that found this recommendation result',
      type: Plugin,
    },
    resourceId: SiloDiscoveryResultKey.fields.resourceId,
    status: SiloDiscoveryResultKey.fields.status,
    title: SiloDiscoveryResultKey.fields.title,
    region: SiloDiscoveryResultKey.fields.region,
    country: SiloDiscoveryResultKey.fields.country,
    countrySubDivision: SiloDiscoveryResultKey.fields.countrySubDivision,
    suggestedCatalog: {
      comment: 'The suggested catalog for this recommendation result',
      type: Catalog,
    },
    rawInputs: {
      comment:
        'The raw inputs that were provided by the plugin as additionalContext and fed into our classifier',
      type: PlaintextContext,
      list: true,
    },
    plaintextContext: {
      comment: 'The plaintext that we will pass into recommendation result',
      type: 'string',
      optional: true,
    },
    pluginConfigurations: {
      comment: 'The plugin configurations for the recommendation result',
      type: DataSiloPluginConfigurations,
      optional: true,
    },
    containsSensitiveData: {
      comment: 'Whether the current resource contains sensitive data',
      type: { ContainsSensitiveData },
      optional: true,
    },
    owners: {
      comment: 'The owners of the silo which discovered the current resource',
      type: UserPreview,
      list: true,
      optional: true,
    },
  },
});

/** Override type */
export type SiloDiscoveryResult = SchemaToType<typeof SiloDiscoveryResult>;

export const MarkDataSiloDiscoveryResultsJunkInput = mkInput({
  name: 'MarkDataSiloDiscoveryResultsJunkInput',
  comment: 'Input for marking data silo discovery results as junk',
  fields: {
    id: SiloDiscoveryResultKey.fields.id,
  },
});

/** Override type */
export type MarkDataSiloDiscoveryResultsJunkInput = SchemaToType<
  typeof MarkDataSiloDiscoveryResultsJunkInput
>;

export const MarkDataSiloDiscoveryResultsDuplicateInput = mkInput({
  name: 'MarkDataSiloDiscoveryResultsDuplicateInput',
  comment: 'Input for marking data silo discovery results as duplicate',
  fields: {
    ...MarkDataSiloDiscoveryResultsJunkInput.fields,
    dataSiloId: {
      comment:
        'ID of an existing data silo needed for marking recommendation as a duplicate of',
      type: 'id',
      modelName: 'dataSilo',
    },
  },
});

/** Override type */
export type MarkDataSiloDiscoveryResultsDuplicateInput = SchemaToType<
  typeof MarkDataSiloDiscoveryResultsDuplicateInput
>;

export const UpdateDataSiloDiscoveryResultsInput = mkInput({
  name: 'UpdateDataSiloDiscoveryResultsInput',
  comment: 'Input for updating data silo discovery results',
  fields: {
    id: SiloDiscoveryResultKey.fields.id,
    /** Only updateable field for v1 */
    suggestedDataSiloType: {
      ...SiloDiscoveryResultKey.fields.suggestedDataSiloType,
      optional: true,
    },
  },
});

/** Override type */
export type UpdateDataSiloDiscoveryResultsInput = SchemaToType<
  typeof UpdateDataSiloDiscoveryResultsInput
>;

export const SiloDiscoveryResultFiltersInput = mkInput({
  name: 'SiloDiscoveryResultFiltersInput',
  comment:
    'Input for filtering a list of system discovery recommendation results',
  fields: {
    ids: {
      comment: 'Filter system discovery recommendation results by ids',
      type: 'id',
      modelName: 'dataSiloDiscoveryResult',
      optional: true,
      list: true,
    },
    text: {
      comment: 'Filter system discovery recommendation results by title',
      type: 'string',
      optional: true,
    },
    pluginIds: {
      comment:
        'Filter system discovery recommendation results by the plugin that discovered it',
      type: 'id',
      modelName: 'plugin',
      optional: true,
      list: true,
    },
    containsSensitiveData: {
      comment: 'Whether the current resource contains sensitive data',
      type: { ContainsSensitiveData },
      optional: true,
      list: true,
    },
    status: {
      comment: 'Filter system discovery recommendation results by status',
      type: { SiloDiscoveryResultStatus },
      optional: true,
      list: true,
    },
  },
});

/** Override type */
export type SiloDiscoveryResultFiltersInput = SchemaToType<
  typeof SiloDiscoveryResultFiltersInput
>;

/**
 * Order for a System Discovery Results query
 */
export const SiloDiscoveryResultOrder = mkOrder(
  SiloDiscoveryResult.name,
  SiloDiscoveryResultOrderField,
);

/** Override type */
export type SiloDiscoveryResultOrder = SchemaToType<
  typeof SiloDiscoveryResultOrder
>;
